import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';


import { InbetriebnahmeService } from '../../../Services/inbetriebnahme.service';
import { InbetriebnahmeDataService } from '../../../Services/offline/inbetriebnahme-data.service';
import { OfflineDownloadService } from '../../../Services/offline/offline-download.service';
import { OfflineSyncService } from '../../../Services/offline/offline-sync.service';
import { MessstellenListeSummary } from '../../../Models/messstelle.model';
import { Datenquelle } from '../../../Models/offline.model';
import { AuthService } from '../../../api-authorization/authorize.service';

/** Übersicht aller importierten Messstellenlisten / Inbetriebnahmen. */
@Component({
  selector: 'app-inbetriebnahme',
  templateUrl: './inbetriebnahme.component.html',
  styleUrls: ['./inbetriebnahme.component.css']
})
export class InbetriebnahmeComponent implements OnInit, OnDestroy {
  listen: MessstellenListeSummary[] = [];
  isLoading = true;
  loadError = '';

  /** Woher die Übersicht stammt. Offline sind nur die geladenen Inbetriebnahmen dabei. */
  quelle: Datenquelle = 'server';

  /** Listen, die über ihr Projekt lokal vorliegen – für das Kennzeichen auf der Kachel. */
  private offlineListen = new Set<number>();
  private offlineAbo?: Subscription;

  constructor(
    private data: InbetriebnahmeDataService,
    private service: InbetriebnahmeService,
    private download: OfflineDownloadService,
    private sync: OfflineSyncService,
    private router: Router,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) { }

  /** Montage/Vorarbeiter dürfen nur lesen – Import und Löschen ausblenden. */
  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  get istOffline(): boolean {
    return this.quelle === 'offline';
  }

  ngOnInit(): void {
    // Beim Öffnen der Übersicht ist ein guter Zeitpunkt, Liegengebliebenes
    // loszuwerden – ohne dass jemand einen Knopf drücken muss.
    void this.sync.synchronisiere('start');

    this.offlineAbo = this.download.offlineStatus$.subscribe(status => {
      this.offlineListen = new Set(status.filter(s => s.verfuegbar).map(s => s.listeId));
    });

    this.load();
  }

  ngOnDestroy(): void {
    this.offlineAbo?.unsubscribe();
  }

  istOfflineVerfuegbar(listeId: number): boolean {
    return this.offlineListen.has(listeId);
  }

  load(): void {
    this.isLoading = true;
    this.loadError = '';

    this.data.getListen().subscribe({
      next: ergebnis => {
        this.listen = ergebnis.daten ?? [];
        this.quelle = ergebnis.quelle;
        this.isLoading = false;

        if (ergebnis.quelle === 'offline' && this.listen.length === 0) {
          this.loadError =
            'Ohne Verbindung stehen nur Inbetriebnahmen zur Verfügung, '
            + 'die vorher offline verfügbar gemacht wurden.';
        }
      },
      error: () => {
        this.loadError = 'Die Messstellenlisten konnten nicht geladen werden.';
        this.isLoading = false;
      }
    });
  }

  // ── Status ──

  /** Eine Inbetriebnahme ist noch nicht gestartet, solange keine Messstelle Prüfpunkte besitzt. */
  istNichtGestartet(inb: MessstellenListeSummary): boolean {
    return (inb.messstellenMitPruefpunkten ?? 0) === 0;
  }

  /** Geprüft ist die Inbetriebnahme erst, wenn wirklich jede Messstelle abgeschlossen ist. */
  istFertig(inb: MessstellenListeSummary): boolean {
    const gesamt = inb.messstellenCount ?? 0;
    const abgeschlossen = inb.messstellenAbgeschlossen ?? 0;

    return gesamt > 0 && abgeschlossen >= gesamt;
  }

  /** Offen = bereits begonnen, aber noch nicht vollständig geprüft. */
  istOffen(inb: MessstellenListeSummary): boolean {
    return !this.istNichtGestartet(inb) && !this.istFertig(inb);
  }

  inbetriebnahmeStatusText(inb: MessstellenListeSummary): string {
    if (this.istFertig(inb)) {
      return 'Geprüft';
    }

    if (this.istNichtGestartet(inb)) {
      return 'Nicht gestartet';
    }

    return 'Offen';
  }

  inbetriebnahmeProzent(inb: MessstellenListeSummary): number {
    return inb.fortschrittProzent ?? 0;
  }

  // ── Navigation ──

  openImport(): void {
    this.router.navigate(['/wartung/inbetriebnahme/import']);
  }

  /** Leere Liste ohne Excel – mit eigener Spaltenstruktur oder aus einer Vorlage. */
  openNeueListe(): void {
    this.router.navigate(['/wartung/inbetriebnahme/neu']);
  }

  openVorlagen(): void {
    this.router.navigate(['/wartung/inbetriebnahme/vorlagen']);
  }

  openListe(liste: MessstellenListeSummary): void {
    this.router.navigate(['/wartung/inbetriebnahme/liste', liste.id]);
  }

  // ── Löschen ──

  deleteListe(liste: MessstellenListeSummary, event: Event): void {
    event.stopPropagation();

    const frage = `Messstellenliste "${liste.name}" mit ${liste.messstellenCount} Messstellen wirklich löschen?`;

    if (!confirm(frage)) {
      return;
    }

    this.service.deleteListe(liste.id).subscribe({
      next: () => {
        this.listen = this.listen.filter(l => l.id !== liste.id);
        this.snackBar.open('Messstellenliste gelöscht.', 'OK', { duration: 3000 });

        // Den lokalen Bestand mit entfernen – die Inbetriebnahme gibt es
        // nicht mehr, ihre Kopie wäre nur noch Ballast.
        void this.download.entferne(liste.id);
      },
      error: () => {
        this.snackBar.open('Löschen fehlgeschlagen.', 'OK', { duration: 4000 });
      }
    });
  }

  trackByInbetriebnahme(_index: number, inb: MessstellenListeSummary): number {
    return inb.id;
  }
}
